import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Divider, Typography } from 'antd';
import {
    ProjectOutlined,
    FileTextOutlined,
    OrderedListOutlined,
    DollarCircleOutlined,
    TeamOutlined,
    LayoutOutlined,
    BuildOutlined,
    TagsOutlined,
    EditOutlined,
    UserOutlined,
    LockOutlined,
    LogoutOutlined,
    HomeOutlined,
    SolutionOutlined,
    HistoryOutlined,
    SettingOutlined,
    MessageOutlined,
    SafetyCertificateOutlined,
    InboxOutlined,
    CheckCircleOutlined,
    CalendarOutlined,
    AppstoreOutlined,
    ApartmentOutlined,
    SearchOutlined,
    ContactsOutlined,
    PartitionOutlined,
    AudioOutlined
} from '@ant-design/icons';
import {
    canAtLeast,
    clearCachedPageAccess,
    getCachedPageAccess,
    pathToPageKey
} from '../utils/pageAccess';
import { clearInstallerSession } from '../utils/installerSession';

const { Sider } = Layout;
const { Title } = Typography;

const Sidebar = ({ isOpen }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [accessTick, setAccessTick] = useState(0);

    useEffect(() => {
        const refresh = () => setAccessTick(tick => tick + 1);
        window.addEventListener('page-access', refresh);
        return () => window.removeEventListener('page-access', refresh);
    }, []);

    const currentRoleId = (() => {
        try {
            const user = JSON.parse(
                localStorage.getItem('user') || 'null'
            );
            const rawRole =
                user?.role_id ??
                user?.roleId ??
                localStorage.getItem('roleId');
            return Number(rawRole);
        } catch {
            return null;
        }
    })();

    const isAdmin = currentRoleId === 1;
    const canOpenMax =
        isAdmin ||
        canAtLeast(
            getCachedPageAccess()?.['max-messenger'],
            'view'
        );
    const isHrAccess =
        isAdmin || currentRoleId === 6 || currentRoleId === 8;
    const canManageUsers =
        isAdmin || currentRoleId === 7;

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('lastActivityAt');
        localStorage.removeItem('lastTokenRefreshAt');
        clearCachedPageAccess();
        clearInstallerSession();
        navigate('/login');
    };

    const canSeePath = path => {
        if (isAdmin) return true;
        const key = pathToPageKey(path);
        if (!key) return true;
        const access = getCachedPageAccess();
        if (!access) return true;
        return canAtLeast(access[key], 'view');
    };

    const rawMenuItems = currentRoleId === 5
        ? [
            {
                key: '/installer/work',
                icon: <BuildOutlined />,
                label: (
                    <Link to="/installer/work">
                        Работа
                    </Link>
                )
            },
            {
                key: '/installer/tmc',
                icon: <InboxOutlined />,
                label: (
                    <Link to="/installer/tmc">
                        Моё ТМЦ
                    </Link>
                )
            },
            {
                key: '/installer',
                icon: <SolutionOutlined />,
                label: (
                    <Link to="/installer">
                        Табель
                    </Link>
                )
            }
        ]
        : currentRoleId === 6 || currentRoleId === 8
        ? [
            { key: '/hr/deals', icon: <SolutionOutlined />, label: <Link to="/hr/deals">Сделки HR</Link> },
            { key: '/hr/staff-registry', icon: <ContactsOutlined />, label: <Link to="/hr/staff-registry">Учёт персонала</Link> },
            { key: '/bitrix-analytics', icon: <TeamOutlined />, label: <Link to="/bitrix-analytics">HR-аналитика</Link> },
            { key: '/calendar', icon: <CalendarOutlined />, label: <Link to="/calendar">Календарь</Link> },
            { key: '/bitrix-tasks', icon: <CheckCircleOutlined />, label: <Link to="/bitrix-tasks">Задачи Bitrix</Link> },
            { key: '/chat', icon: <MessageOutlined />, label: <Link to="/chat">Чат</Link> },
            { key: '/transcription', icon: <AudioOutlined />, label: <Link to="/transcription">Транскрибация</Link> }
        ]
        : currentRoleId === 7
        ? [
            { key: '/users', icon: <UserOutlined />, label: <Link to="/users">Пользователи</Link> },
            { key: '/org-structure', icon: <ApartmentOutlined />, label: <Link to="/org-structure">Отделы и должности</Link> },
            { key: '/user-activity', icon: <HistoryOutlined />, label: <Link to="/user-activity">Журнал доступа</Link> },
            { key: '/calendar', icon: <CalendarOutlined />, label: <Link to="/calendar">Календарь</Link> },
            { key: '/bitrix-tasks', icon: <CheckCircleOutlined />, label: <Link to="/bitrix-tasks">Задачи Bitrix</Link> },
            { key: '/chat', icon: <MessageOutlined />, label: <Link to="/chat">Чат</Link> }
        ]
        : [
        { key: '/dashboard', icon: <HomeOutlined />, label: <Link to="/dashboard">Главная</Link> },
        { key: '/tasks', icon: <ProjectOutlined />, label: <Link to="/tasks">Задачи</Link> },
        { key: '/bitrix-tasks', icon: <CheckCircleOutlined />, label: <Link to="/bitrix-tasks">Задачи Bitrix</Link> },
        { key: '/calendar', icon: <CalendarOutlined />, label: <Link to="/calendar">Календарь</Link> },
        {
            key: '/task-manager',
            icon: <AppstoreOutlined />,
            label: (
                <Link to="/task-manager">
                    Менеджер задач
                </Link>
            )
        },
        {
            key: '/task-manager/settings',
            icon: <SettingOutlined />,
            label: (
                <Link to="/task-manager/settings">
                    Настройки TM
                </Link>
            )
        },
        { label: 'Документы', key: '/docs', icon: <SolutionOutlined /> },
        { label: 'Согласование', key: '/doc-workflows', icon: <CheckCircleOutlined /> },
        { key: '/subcontracts', icon: <PartitionOutlined />, label: <Link to="/subcontracts">Субподряды</Link> },

        {
            key: '/invoice-approval',
            icon: <DollarCircleOutlined />,
            label: (
                <Link to="/invoice-approval">
                    Согласование счетов
                </Link>
            )
        },
        { label: 'Чат', key: '/chat', icon: <MessageOutlined /> },
        { key: '/transcription', icon: <AudioOutlined />, label: <Link to="/transcription">Транскрибация</Link> },
        { label: 'Ежедневные отчеты', key: '/daily-reports', icon: <SolutionOutlined /> },
        { key: '/financial-articles', icon: <FileTextOutlined />, label: <Link to="/financial-articles">Статьи</Link> },
        { key: '/nomenclature', icon: <OrderedListOutlined />, label: <Link to="/nomenclature">Номенклатура</Link> },
        { key: '/tmc', icon: <InboxOutlined />, label: <Link to="/tmc">ТМЦ</Link> },
        ...(currentRoleId === 1 || currentRoleId === 2
            ? [
                {
                    key: '/customer-order-compare',
                    icon: <CheckCircleOutlined />,
                    label: (
                        <Link to="/customer-order-compare">
                            Проверка заказа покупателя
                        </Link>
                    )
                },
                {
                    key: '/bp-closing',
                    icon: <CalendarOutlined />,
                    label: (
                        <Link to="/bp-closing">
                            Контроль закрытия
                        </Link>
                    )
                }
            ]
            : []),
        { key: '/invoices', icon: <DollarCircleOutlined />, label: <Link to="/invoices">ВДР</Link> },
        { key: '/counterparties', icon: <TeamOutlined />, label: <Link to="/counterparties">Контрагенты</Link> },
        { key: '/templates-incomerce', icon: <LayoutOutlined />, label: <Link to="/templates-incomerce">Шаблоны</Link> },
        { key: '/constructor', icon: <BuildOutlined />, label: <Link to="/constructor">Сценарии</Link> },
        { key: '/price-types', icon: <TagsOutlined />, label: <Link to="/price-types">Типы цен</Link> },
        { key: '/edit-stat', icon: <EditOutlined />, label: <Link to="/edit-stat">Переменные</Link> },
        ...(canManageUsers
            ? [
                { key: '/users', icon: <UserOutlined />, label: <Link to="/users">Пользователи</Link> },
                { key: '/org-structure', icon: <ApartmentOutlined />, label: <Link to="/org-structure">Отделы и должности</Link> },
                { key: '/user-activity', icon: <HistoryOutlined />, label: <Link to="/user-activity">Журнал доступа</Link> }
            ]
            : []),
        ...(canOpenMax
            ? [
                { key: '/max-messenger', icon: <MessageOutlined />, label: <Link to="/max-messenger">MAX</Link> }
            ]
            : []),
        ...(isAdmin
            ? [
                { key: '/priveleg', icon: <LockOutlined />, label: <Link to="/priveleg">Права</Link> },
                { key: '/keyword-watch', icon: <SearchOutlined />, label: <Link to="/keyword-watch">Мониторинг слов</Link> },
                { key: '/goskey', icon: <SafetyCertificateOutlined />, label: <Link to="/goskey">Госключ</Link> }
            ]
            : []),
        ...(isHrAccess
            ? [
                { key: '/hr/deals', icon: <SolutionOutlined />, label: <Link to="/hr/deals">Сделки HR</Link> },
                { key: '/hr/staff-registry', icon: <ContactsOutlined />, label: <Link to="/hr/staff-registry">Учёт персонала</Link> },
                { key: '/bitrix-analytics', icon: <TeamOutlined />, label: <Link to="/bitrix-analytics">HR-аналитика</Link> }
            ]
            : [])
    ];

    const menuItems = useMemo(() => {
        const filterItems = items =>
            (items || [])
                .map(item => {
                    if (item.children) {
                        const children = filterItems(
                            item.children
                        );
                        if (!children.length) return null;
                        return { ...item, children };
                    }
                    if (
                        item.key &&
                        String(item.key).startsWith('/') &&
                        !canSeePath(item.key)
                    ) {
                        return null;
                    }
                    return item;
                })
                .filter(Boolean);

        return filterItems(rawMenuItems);
    }, [currentRoleId, location.pathname, accessTick, canOpenMax]);

    return (
        <Sider
            trigger={null}
            collapsible
            collapsed={!isOpen} // Если isOpen false, то сайдбар сворачивается
            collapsedWidth={0}   // Сайдбар полностью исчезает при закрытии
            width={250}
            theme="light"
            style={{
                height: '100vh',
                position: 'fixed',
                left: 0,
                top: 0,
                bottom: 0,
                boxShadow: isOpen ? '2px 0 8px 0 rgba(29,35,41,.05)' : 'none',
                zIndex: 1001,
                overflow: 'hidden',
                transition: 'all 0.3s ease'
            }}
        >
            {/* Заголовок (лого) появляется только если открыто */}
            <div style={{ 
                height: '64px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                padding: '0 16px',
                whiteSpace: 'nowrap'
            }}>
                <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
                    CRM System
                </Title>
            </div>

            <Divider style={{ margin: '4px 0' }} />

            {/* Меню с иконками и текстом */}
            <Menu
                mode="inline"
                selectedKeys={[location.pathname]}
                items={menuItems}
                style={{ 
                    height: 'calc(100vh - 145px)', 
                    borderRight: 0, 
                    overflowY: 'auto' 
                }}
            />

            <Divider style={{ margin: '4px 0' }} />

            {/* Кнопка выхода */}
            <div style={{ padding: '8px 16px' }}>
                <Button
                    type="text"
                    danger
                    icon={<LogoutOutlined />}
                    onClick={handleLogout}
                    block
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        height: '40px'
                    }}
                >
                    <span style={{ marginLeft: 8 }}>Выйти</span>
                </Button>
            </div>
        </Sider>
    );
};

export default Sidebar;